import {
  ActionIcon,
  Affix,
  Button,
  Group,
  Popover,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useMatches,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { Modal } from "@/components/elements";
import { IconChat, IconX } from "@/components/icons";
import { useSubmitFeedback } from "@/services/feedback/user-feedback";
import { useProfile } from "@/services/profile/get-profile";

import type { Attachment } from "@/types/attachment";

export type UserFeedbackProps = {
  opened?: boolean;
  image?: Attachment | null;
  onOpenedChange?: (opened: boolean) => void;
  title?: string;
  description?: string;
};

export const UserFeedback = ({
  opened: controlledOpened,
  image,
  onOpenedChange,
  title,
  description,
}: UserFeedbackProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data: user } = useProfile();
  const [internalOpened, setInternalOpened] = useState(false);
  const [comment, setComment] = useState("");

  const opened = controlledOpened ?? internalOpened;

  const setOpened = (value: boolean) => {
    if (onOpenedChange) {
      onOpenedChange(value);
    } else {
      setInternalOpened(value);
    }
  };

  const isMobile = useMatches({ base: true, sm: false });

  const { mutate, isPending } = useSubmitFeedback({
    mutationConfig: {
      onSuccess: () => {
        notifications.show({
          color: "green",
          title: t("feedback.success_title"),
          message: t("feedback.success_message"),
        });
        setComment("");
        setOpened(false);
      },
      onError: () => {
        notifications.show({
          color: "red",
          title: t("feedback.error_title"),
          message: t("feedback.error_message"),
        });
      },
    },
  });

  if (!user) {
    return null;
  }

  const handleSubmit = () => {
    const trimmedComment =
      `User: ${user.id}, email: ${user.email}, name: ${user.name}, image_id: ${image?.id ?? "no-id"}, image_name: ${image?.name ?? ""}, image_path: ${image?.path ?? "no-path"}, message: ` +
      comment.trim();

    if (!trimmedComment) {
      return;
    }

    mutate({
      data: {
        page_reference: pathname,
        comment: trimmedComment,
      },
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setOpened(false);
    }
  };

  const feedbackTitle = title ?? t("feedback.title");
  const feedbackDescription = description ?? t("feedback.description");

  const feedbackForm = (
    <Stack gap="lg" p={isMobile ? undefined : "md"}>
      <Stack gap="sm">
        {!isMobile && (
          <Text fw={600} size="lg">
            {feedbackTitle}
          </Text>
        )}
        <Text size="sm" c="dimmed">
          {feedbackDescription}
        </Text>
      </Stack>

      <Textarea
        label={t("feedback.message_label")}
        placeholder={t("feedback.message_placeholder")}
        value={comment}
        onChange={(event) => setComment(event.currentTarget.value)}
        minRows={4}
        autosize
        withAsterisk
        disabled={isPending}
        mah="50dvh"
        style={{
          overflow: "auto",
        }}
      />

      <Group justify="flex-end" gap="md">
        <Button variant="transparent" color="danger" onClick={handleClose} disabled={isPending}>
          {t("common.cancel")}
        </Button>
        <Button onClick={handleSubmit} loading={isPending} disabled={!comment.trim()}>
          {t("feedback.submit")}
        </Button>
      </Group>
    </Stack>
  );

  const feedbackButton = (
    <ActionIcon
      variant="filled"
      color="primary"
      size="xl"
      radius="xl"
      aria-label={t("feedback.tooltip")}
      onClick={() => setOpened(isMobile ? true : !opened)}
    >
      {!isMobile && opened ? <IconX /> : <IconChat size={24} />}
    </ActionIcon>
  );

  return (
    <Affix position={{ bottom: 10, right: 20 }} zIndex={100}>
      {isMobile ? (
        <>
          {feedbackButton}
          <Modal
            centered
            large
            title={t("feedback.title")}
            titleFontSize="1rem"
            opened={opened}
            onClose={handleClose}
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
          >
            {feedbackForm}
          </Modal>
        </>
      ) : (
        <Popover
          opened={opened}
          onChange={setOpened}
          position="top-end"
          width={320}
          shadow="md"
          withArrow
          closeOnEscape={!isPending}
          closeOnClickOutside={!isPending}
        >
          <Popover.Target>
            <Tooltip label={t("feedback.tooltip")} position="left">
              {feedbackButton}
            </Tooltip>
          </Popover.Target>

          <Popover.Dropdown mah="90dvh">{feedbackForm}</Popover.Dropdown>
        </Popover>
      )}
    </Affix>
  );
};
